import React, { useState } from 'react';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';

const InsightsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'React', 'Next.js', 'AI & ML', 'Cloud & Architecture', 'E-Commerce'];

  const posts = [
    {
      title: 'The Future of Web Engineering: What\'s Coming in 2026',
      excerpt: 'Explore the latest features and architectural patterns in React 19, Server Components, AI-assisted rendering, and performance optimization.',
      category: 'React',
      author: 'Sarah Johnson',
      date: 'Jan 15, 2026',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&q=80',
      readTime: '5 min read'
    },
    {
      title: 'Building Scalable Enterprise Web Applications with Next.js',
      excerpt: 'Learn best practices for building enterprise-grade applications using Next.js, including SSR, API routes, edge caching, and security auditing.',
      category: 'Next.js',
      author: 'Michael Chen',
      date: 'Jan 12, 2026',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
      readTime: '8 min read'
    },
    {
      title: 'AI Integration in Modern Enterprise Software',
      excerpt: 'Discover how to integrate Large Language Models (LLMs) and custom RAG pipelines into web applications to automate workflows.',
      category: 'AI & ML',
      author: 'Emily Rodriguez',
      date: 'Jan 10, 2026',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      readTime: '6 min read'
    },
    {
      title: 'Cloud Native Microservices & Serverless Scaling',
      excerpt: 'Architecting high-availability cloud infrastructure on AWS for microservice applications serving millions of active users.',
      category: 'Cloud & Architecture',
      author: 'David Miller',
      date: 'Jan 05, 2026',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80',
      readTime: '7 min read'
    },
    {
      title: 'Headless E-Commerce: Driving High Conversion Rates',
      excerpt: 'Why modern retail brands are transitioning to headless store architecture with custom frontend storefronts and API integration.',
      category: 'E-Commerce',
      author: 'Rachel Adams',
      date: 'Dec 28, 2025',
      image: 'https://images.unsplash.com/photo-1556740758-90de374c12ad?w=800&q=80',
      readTime: '6 min read'
    },
    {
      title: 'Mobile App Performance Optimization Strategies',
      excerpt: 'Proven techniques for reducing bundle size, optimizing frame rates, and speeding up startup times in React Native and Flutter.',
      category: 'React',
      author: 'Alex Rivera',
      date: 'Dec 20, 2025',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80',
      readTime: '5 min read'
    }
  ];

  const filteredPosts = selectedCategory === 'All'
    ? posts
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="pt-24 bg-gray-950 min-h-screen text-left">
      {/* Hero Header */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
            <BookOpen className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
              Tech Insights & Articles
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
            Latest From Our <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Engineering Blog</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            Deep-dive technical insights, web architecture trends, AI implementation strategies, and software growth guides from our engineering team.
          </p>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2.5 mt-8">
            {categories.map((cat, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedCategory(cat)}
                className={`px-5 py-2.5 rounded-xl text-sm md:text-sm font-semibold transition-all duration-300 ${selectedCategory === cat
                    ? 'bg-gradient-to-r from-orange-500 to-cyan-500 text-white shadow-lg shadow-orange-500/20'
                    : 'bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'
                  }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Articles Grid */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <article
                key={index}
                className="group bg-gradient-to-b from-gray-900/90 to-gray-900/50 backdrop-blur-md border border-gray-800 rounded-2xl overflow-hidden hover:border-cyan-500/60 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className="relative h-48 overflow-hidden bg-gray-900">
                    <img
                      src={post.image}
                      alt={post.title}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80';
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-orange-500 text-white text-sm font-semibold px-3 py-1 rounded-full shadow-md">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-gray-300 text-sm md:text-sm mb-4 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-400 mb-4 pt-3 border-t border-gray-800">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center space-x-1">
                          <User className="w-3.5 h-3.5 text-cyan-400" />
                          <span>{post.author}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                          <span>{post.date}</span>
                        </span>
                      </div>
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  <button className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 text-sm font-bold transition-colors group/btn">
                    <span>Read Full Article</span>
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
